import { P } from "./pasosAsistente";

/**
 * En qué paso del asistente se retoma una campaña que se abre por su id («Seguir armando la campaña» del
 * detalle, `/activa/real?campana=<id>`). Antes ese botón abría `/activa/real` a secas: el asistente
 * retomaba el último borrador de la marca, que podía ser de otra campaña (y después de 24 h, ninguno).
 *
 * `null` = ya salió: no se retoma en el asistente (sería editar lo que la gente ya recibió), se va al detalle.
 */
export function pasoParaRetomar(estado: string | null | undefined): number | null {
  switch (estado) {
    case "enviando":
    case "sent":
    case "measured":
      return null;
    case "programada":
      return P.envio;
    case "messaged":
      return P.mensajes;
    case "context":
      return P.plan;
    default:
      // analyzed (o un estado que no conocemos): el reporte, que es lo primero después de analizar
      return P.reporte;
  }
}

/**
 * Dónde abre el asistente una campaña pedida por id, con el borrador de ESA campaña si quedó uno en el navegador.
 * `null` = ya salió (al detalle).
 *
 * Con un borrador de la misma campaña se abría en el paso guardado sin mirar el estado: si la última vez se había
 * quedado en «Cargar datos» (después de «Volvé a subir los archivos», o yendo hacia atrás para mirar), «Seguir
 * armando» abría el paso 1 de una campaña con los mensajes ya armados (QA del 08/10). Manda el estado; el borrador
 * sólo gana si iba MÁS ADELANTE (el tono o la oferta elegidos y todavía sin guardar, que se guardan recién en «Por
 * dónde y cuándo»), y nunca hasta Resultados, que es de una campaña que salió. Lo visitado del borrador se conserva.
 */
export function pasoAlAbrir(
  estado: string | null | undefined,
  borrador?: { current: number; maxReached: number } | null,
): { current: number; maxReached: number } | null {
  const paso = pasoParaRetomar(estado);
  if (paso === null) return null;
  const current = borrador && borrador.current > paso && borrador.current < P.resultados ? borrador.current : paso;
  return { current, maxReached: Math.max(current, borrador?.maxReached ?? 0) };
}

export const NO_ENCONTRADA = "No encontramos esa campaña: puede que la hayan borrado.";
export const NO_SE_PUDO_ABRIR = "No pudimos abrir esa campaña. Probá de nuevo en un rato.";

/**
 * Qué decir cuando `/activa/real?campana=<id>` no puede abrir la campaña. `req()` (`cn/api.ts`) LANZA con los
 * errores que no son 422, con el `status` adentro: el 404 de una campaña que no existe (o que se borró) caía en
 * el `catch` y decía «Probá de nuevo en un rato», que invita a reintentar algo que nunca va a andar (QA del 08/10).
 */
export function mensajeAlNoAbrir(e: unknown): string {
  return (e as { status?: number } | null)?.status === 404 ? NO_ENCONTRADA : NO_SE_PUDO_ABRIR;
}

/**
 * Si lo que está en el asistente son los datos de ejemplo. Con la campaña ya analizada manda el backend
 * (`campaign.esEjemplo`, el mismo dato con el que `send` se niega); antes de analizar, los archivos cargados.
 *
 * Se derivaba sólo de los archivos, y los de ejemplo quedan guardados en el navegador (vienen con la app).
 * Al abrir por id una campaña real después de haber probado con el ejemplo, el borrador re-adjuntaba esos
 * archivos: la campaña real quedaba marcada «Son datos de ejemplo», no se podía lanzar y el botón ofrecía
 * descartarla.
 */
export function esDeEjemplo(
  campaign: { esEjemplo?: boolean } | null | undefined,
  archivoClientes: string | undefined,
  clientesDeEjemplo: string,
): boolean {
  if (campaign) return campaign.esEjemplo === true;
  return !!archivoClientes && archivoClientes === clientesDeEjemplo;
}
